import { IRequestCallback } from '@df/api/request/request.service';
import { IFilterDefinition } from '@df/catalog-ui/listing/listing';
import { IModelDataV1, IModelDataV2, IModelDataV3, Model, TLoadDependeciesParams } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { EdependencyLevel } from '../model';

export interface ICategoryDataV1 extends IModelDataV1 {
    id: number;
    name: string;
    position: number;
    parent: number;
}

export interface ICategoryDataV2 extends ICategoryDataV1, IModelDataV2 {
    id: number;
    level: number;
    short_description?: string;
    image?: string;
    products: number[];
    url?: string;
}

export interface ICategoryDataV3 extends ICategoryDataV2, IModelDataV3 {
    id: number;

    filters?: IFilterDefinition[];
    name: string;
    description?: string;
    mobile_image?: string;
    text_color?: string;
    seo_header?: string;
    seo_description?: string;
    // products to place on top positions
    heroProducts?: number[];
    /**
     * If OOS should be pushed to bottom
     */
    push_oos_to_bottom?: boolean;
}

export declare type ICategoryData = ICategoryDataV1 | ICategoryDataV2 | ICategoryDataV3;

// export interface ICategoryLoadOptions extends ILoadOptions {
//     productVerbosity?: number;
// }

/**
 * Category model representing magento category
 */
export class Category<T extends ICategoryData = ICategoryData> extends Model<T, number> {
    /**
     * Object type id
     * @type {String}
     */
    static override type = 'category';

    /**
     * If model is routable
     * @type {Boolean}
     */
    static override routable = true;

    protected childrenLoaded?: number;
    protected siblingsLoaded?: number;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const loader = params.loader;
        if (this.verbosity! > 2) {
            const data = <ICategoryDataV3>this.data;
            const productVerbosity = 2;
            if (params.dependencyLevel >= EdependencyLevel.details) {
                data.products?.forEach(product =>
                    loader.add(
                        this.resources.product
                            .get(product)
                            .load(productVerbosity, { ...params, safe: true, dependencyLevel: EdependencyLevel.listing })
                    )
                );
                loader.add(this.resources.attribute.loadForProduct(2));
                data.filters?.forEach(i => {
                    if (i.type === 'attribute' && i.source) {
                        loader.add(
                            this.resources.attribute
                                .get(i.source)
                                .load(1, { ...params, safe: true, dependencyLevel: EdependencyLevel.listing })
                        );
                    }
                });
            }
        }
    }

    /**
     * Siblings getter
     *
     * usage:
     *
     * categoryModel.siblings
     *
     */
    get siblings(): Category[] | undefined {
        if (!this.cache.siblings) {
            if (this.data) {
                const parent = this.data.parent;
                this.cache.siblings = this.resources.category.getAllExisting().filter(model => model.data?.parent === parent);
            }
        }
        return this.cache.siblings;
    }

    /**
     * Siblings getter
     *
     * usage:
     *
     * categoryModel.siblings
     *
     */
    get children(): Category[] | undefined {
        if (!this.cache.children) {
            if (this.data) {
                this.cache.children = this.resources.category.getAllExisting().filter(model => model.data?.parent === this.id);
            }
        }
        return this.cache.children;
    }

    /**
     * Siblings getter
     *
     * usage:
     *
     * categoryModel.siblings
     *
     */
    get parent(): Category | undefined {
        return this.data?.parent ? this.resources.category.getExisting(this.data.parent) : undefined;
    }

    /**
     * Product getter
     * @return {Product[]}
     */
    get products(): Product<IProductDataV2>[] {
        return <Product<IProductDataV2>[]>this.resources.product.getAllExisting((<ICategoryDataV2>this.data)?.products || []);
    }

    /**
     * Loads children categories
     * If already loaded will return void otherwise will return promise
     */
    async loadChildren(verbosity: number = 1): Promise<Category[] | undefined> {
        if (!this.childrenLoaded || this.childrenLoaded < verbosity) {
            this.childrenLoaded = verbosity;
            this.cache.children = undefined;
            try {
                return await this.resources.category.find({ filter: { parent: this.id }, verbosity });
            } catch (err) {
                this.childrenLoaded = undefined;
            }
        }
    }

    /*
     * Loads parent category
     */
    loadParent(verbosity: number = 1): Promise<Category> | void {
        if (this.data?.parent) {
            return this.resources.category.load(this.data.parent, verbosity);
        }
    }

    /**
     * Loads siblings categories
     * If already laoded will return void otherwise will return promise
     */
    async loadSiblings(verbosity: number = 1): Promise<Category[] | undefined> {
        if (!this.siblingsLoaded || this.siblingsLoaded < verbosity) {
            this.siblingsLoaded = verbosity;
            this.cache.siblings = undefined;
            const parent = this.data?.parent;
            try {
                return await this.resources.category.find({ filter: { parent }, verbosity });
            } catch (err) {
                // revert loaded flag on error
                this.siblingsLoaded = undefined;
            }
        }
    }

    get ancestors(): Category[] {
        return this.parent ? [this.parent, ...this.parent.ancestors] : [];
    }
}
